import { use } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Profile } from '@brew/shared'
import { supabase } from '@/lib/supabase'
import { AuthContext } from './context'

export function useAuth() {
  const context = use(AuthContext)
  if (!context) throw new Error('useAuth muss innerhalb von <AuthProvider> verwendet werden.')
  return context
}

/** Das Profil zum eingeloggten Nutzer — enthält Anzeigename und Rolle. */
export function useProfile() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['profile', user?.id],
    enabled: Boolean(user),
    queryFn: async (): Promise<Profile> => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user!.id)
        .single()
      if (error) throw error
      return data
    },
  })
}

/** Nach dieser Zeit gilt ein Anmeldeversuch als gescheitert. */
const AUTH_TIMEOUT_MS = 15_000

export class AuthTimeoutError extends Error {
  constructor() {
    super('auth-timeout')
    this.name = 'AuthTimeoutError'
  }
}

/**
 * Bricht ab, wenn Supabase nicht antwortet.
 *
 * Nötig, weil supabase-js bei gedrosselten Anfragen (der eingebaute
 * Auth-Endpunkt limitiert deutlich) intern wiederholt, statt den Fehler
 * durchzureichen — der Aufruf kehrt dann minutenlang nicht zurück und die
 * Oberfläche steht mit laufendem Knopf da, ohne dem Nutzer etwas zu sagen.
 */
async function withTimeout<T>(operation: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout>
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new AuthTimeoutError()), AUTH_TIMEOUT_MS)
  })
  try {
    return await Promise.race([operation, timeout])
  } finally {
    clearTimeout(timer!)
  }
}

export async function signIn(email: string, password: string) {
  const { error } = await withTimeout(supabase.auth.signInWithPassword({ email, password }))
  if (error) throw error
}

/**
 * Legt ein Konto an.
 *
 * Ob danach direkt eine Session besteht, hängt an einer Projekteinstellung
 * ("Confirm email"): ist sie aktiv, kommt die Session erst nach dem Klick im
 * Bestätigungsmail. Der Rückgabewert macht das für die UI unterscheidbar,
 * damit sie unter beiden Einstellungen richtig reagiert.
 */
export async function signUp(
  email: string,
  password: string,
  displayName: string,
): Promise<{ needsEmailConfirmation: boolean }> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { display_name: displayName } },
  })
  if (error) throw error
  return { needsEmailConfirmation: data.session === null }
}

export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}
